import { Card } from '@finansafe/design-system';
import { AlertTriangle, ArrowRight, FileText, Lightbulb, PiggyBank, TrendingUp, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import PageHelmet from '../components/PageHelmet';
import OnboardingModal from '../components/OnboardingModal';
import { authService } from '../services/AuthService';
import { dashboardService, type DashboardCardData } from '../services/DashboardService';
import { useAuth } from '../hooks/useAuth';
import type { User } from '../types/api';
import { getRandomMotivationalPhrase } from '../data/motivationalPhrases';

export default function Dashboard() {
  const { isEnterprise, user: contextUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [cardData, setCardData] = useState<DashboardCardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [motivationalPhrase, setMotivationalPhrase] = useState<string>('');
  const [showMotivationalBanner, setShowMotivationalBanner] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);


  useEffect(() => {
    const loadUserData = async () => {
      try {
        const response = await authService.getCurrentUser();
        if (response.success && response.data) {
          setUser(response.data);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
    // Set a random motivational phrase when component mounts
    setMotivationalPhrase(getRandomMotivationalPhrase());
  }, []);

  // Check onboarding status - use fresh data from API call instead of context
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (user) {
        // Force fresh data from API
        const freshUserData = await authService.getCurrentUser(true);

        if (freshUserData.success && freshUserData.data) {
          const onboardingCompleted = freshUserData.data.onboarding_completed === true;
          const shouldShowOnboarding = !onboardingCompleted;


          setShowOnboarding(shouldShowOnboarding);
        }
      }
    };

    checkOnboardingStatus();
  }, [user, contextUser]);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const cardDataResponse = await dashboardService.getDashboardCardData();

        if (cardDataResponse.success && cardDataResponse.data) {
          setCardData(cardDataResponse.data);
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      }
    };

    loadDashboardData();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  const firstName = user?.name?.split(' ')[0] || '';
  const welcomeMessage = `Olá novamente, ${firstName}! 👋`;

  const handleCloseOnboarding = () => {
    setShowOnboarding(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHelmet
        title="Dashboard - Painel de Controle"
        description="Visão geral das suas finanças e planos. Acesse seus planos de negócio, oportunidades de renda extra e gerencie suas finanças."
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {welcomeMessage}
          </h1>
          <p className="text-gray-600">
            Vamos continuar construindo sua independência financeira
          </p>
        </div>
        {/* Main Modules */}
        <div className="grid lg:grid-cols-3 gap-8 mb-8">
          {/* Finance Management Module */}
          <Card className="group cursor-pointer h-72 flex">
            <Link to="/finance-management/dashboard" className="block w-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <PiggyBank className="w-6 h-6 text-purple-600" />
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {isEnterprise ? "Gestão Financeira Empresarial" : "Gestão Financeira"}
              </h3>
              <p className="text-gray-600 mb-4 flex-1">
                {isEnterprise
                  ? "Acompanhe receitas, despesas, fluxo de caixa e índices de liquidez da sua empresa"
                  : "Acompanhe receitas, despesas e o crescimento dos seus negócios"
                }
              </p>
              <div className="mt-auto space-y-2 pt-4 pb-4 border-t border-gray-200">
                <div className="flex items-start gap-2">
                  {cardData?.finance.insightType === 'alert' ? (
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  ) : cardData?.finance.insightType === 'success' ? (
                    <TrendingUp className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  ) : (
                    <Lightbulb className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  )}
                  <span className="text-sm text-gray-700 leading-relaxed">
                    {cardData?.finance.lastInsight || 'Carregando insights...'}
                  </span>
                </div>
              </div>
            </Link>
          </Card>

          {/* Extra Income Module */}
          <Card className="group cursor-pointer h-72 flex">
            <Link to="/extra-income/introduction" className="block w-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {isEnterprise ? "Plano Estratégico" : "Renda Extra"}
              </h3>
              <p className="text-gray-600 mb-4 flex-1">
                {isEnterprise
                  ? "Desenvolva estratégias personalizadas para crescimento e otimização do seu negócio"
                  : "Descubra novas oportunidades de ganhar dinheiro com suas habilidades"
                }
              </p>
              <div className="mt-auto space-y-2 pt-4 pb-4 border-t border-gray-200">
                {cardData?.extraIncome.hasGeneratedSuggestions ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {isEnterprise ? "Estratégias ativas" : "Sugestões ativas"}
                    </span>
                    <span className="font-medium text-green-600">
                      {cardData.extraIncome.activeSuggestions} {isEnterprise ? "estratégias" : "sugestões"}
                    </span>
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>
                        {isEnterprise
                          ? "Gere seu primeiro plano estratégico personalizado"
                          : "Gere suas primeiras sugestões de renda extra personalizadas"
                        }
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </Link>
          </Card>

          {/* Business Plan Module */}
          <Card className="group cursor-pointer h-72 flex">
            <Link
              to="/my-plans"
              className="block w-full flex flex-col"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Plano de Negócio
              </h3>
              <p className="text-gray-600 mb-4 flex-1">
                Estruture suas ideias em um plano de negócio profissional
              </p>
              <div className="mt-auto space-y-2 pt-4 pb-4 border-t border-gray-200">
                {cardData?.businessPlan.lastPlan ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Último plano</span>
                      <span className="font-medium text-gray-900 truncate ml-2" title={cardData.businessPlan.lastPlan}>
                        {cardData.businessPlan.lastPlan.length > 20
                          ? `${cardData.businessPlan.lastPlan.substring(0, 20)}...`
                          : cardData.businessPlan.lastPlan
                        }
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Progresso</span>
                      <span className="font-medium text-blue-600">
                        {cardData.businessPlan.completionPercentage}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${cardData.businessPlan.completionPercentage}%` }}
                      ></div>
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>Crie seu primeiro plano de negócio</span>
                    </div>
                  </div>
                )}
              </div>
            </Link>
          </Card>
        </div>

        {/* Motivational Banner */}
        {showMotivationalBanner && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 max-w-md mx-auto">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-lg shadow-lg border border-white/20 backdrop-blur-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-medium leading-relaxed">
                    {motivationalPhrase}
                  </p>
                </div>
                <button
                  onClick={() => setShowMotivationalBanner(false)}
                  className="flex-shrink-0 text-white/80 hover:text-white transition-colors p-1"
                  aria-label="Fechar mensagem motivacional"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Onboarding Modal */}
        <OnboardingModal
          isOpen={showOnboarding}
          onClose={handleCloseOnboarding}
          isEnterprise={isEnterprise}
        />
      </div>
    </div>
  );
}
